import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, KeyRound, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authService } from "@/services/auth.service";
import { getErrorMessage } from "@/services/api";

export default function ResetPasswordPage() {
    const [params] = useSearchParams();
    const [token, setToken] = useState(params.get("token") || "");
    const [newPassword, setNewPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    async function submit(e) {
        e.preventDefault();
        if (newPassword.length < 8) {
            toast.error("A senha deve ter pelo menos 8 caracteres.");
            return;
        }
        if (newPassword !== confirm) {
            toast.error("As senhas não coincidem.");
            return;
        }
        setSubmitting(true);
        try {
            await authService.resetPassword(token, newPassword);
            toast.success("Senha redefinida com sucesso. Faça login.");
            navigate("/login", { replace: true });
        } catch (err) {
            toast.error(getErrorMessage(err, "Não foi possível redefinir a senha."));
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="grid min-h-screen place-items-center p-6">
            <Card className="w-full max-w-md rounded-2xl border shadow-sm">
                <CardContent className="space-y-5 p-6 sm:p-8">
                    <Link
                        to="/login"
                        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                    >
                        <ChevronLeft className="h-3.5 w-3.5" /> Voltar ao login
                    </Link>
                    <div className="space-y-1">
                        <div className="text-2xl font-semibold tracking-tight">Definir nova senha</div>
                        <p className="text-sm text-muted-foreground">
                            Escolha uma senha forte (mínimo 8 caracteres).
                        </p>
                    </div>
                    <form onSubmit={submit} className="space-y-4">
                        <div className="space-y-1.5">
                            <Label htmlFor="token">Token</Label>
                            <Input
                                id="token"
                                value={token}
                                onChange={(e) => setToken(e.target.value)}
                                required
                                data-testid="reset-token-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="new">Nova senha</Label>
                            <Input
                                id="new"
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                minLength={8}
                                data-testid="reset-new-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="confirm">Confirmar senha</Label>
                            <Input
                                id="confirm"
                                type="password"
                                value={confirm}
                                onChange={(e) => setConfirm(e.target.value)}
                                required
                                minLength={8}
                                data-testid="reset-confirm-input"
                            />
                        </div>
                        <Button
                            type="submit"
                            className="w-full bg-secondary text-secondary-foreground hover:opacity-90"
                            disabled={submitting}
                            data-testid="reset-submit"
                        >
                            {submitting ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            ) : (
                                <KeyRound className="mr-2 h-4 w-4" />
                            )}
                            Salvar nova senha
                        </Button>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
